import { MatPaginator } from '@angular/material/paginator';
import { PageEvent } from '@angular/material/paginator';
import { Component, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { SupplierService } from '../Services/supplier.service';
import { Supplier, SupplierResponse } from '../Models/Supplier';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SUPPLIER_DISPLAYED_COLUMNS } from '../Constants/supplier-table-columns';
import { FIELD_OPTIONS, OPERATOR_OPTIONS } from '../Constants/search-options';
import { AuthService } from '../Services/auth.service';

@Component({
  selector: 'app-supplier-search',
  templateUrl: './supplier-search.component.html',
  styleUrls: ['./supplier-search.component.css']
})
export class SupplierSearchComponent implements AfterViewInit {
  searchText: string = '';
  selectedOption: string = '';
  showError: boolean = false;
  disableAndForUpcomingRows = false;
  showOperator = false;
  labelText = 'Supplier Search';
  statement = '*If you cannot locate your vendor on this site, it is possible they are currently being onboarded as a vendor to Sky. To verify, please log in to Ariba, navigate to the Supplier Management tab, and search for your vendor.';
  isLoading = false;
  hasRecords = false;

  suppliers = new MatTableDataSource<Supplier>([]);

  pageNumber = 1;
  pageSize = 5;
  totalRecords = 0;

  displayedColumns = SUPPLIER_DISPLAYED_COLUMNS;

  onSelectionChange() {
    console.log('Selected option:', this.selectedOption);
  }

  dataSource = new MatTableDataSource(this.suppliers.data);

  form: FormGroup;
  fieldOptions = FIELD_OPTIONS;
  operatorOptions = OPERATOR_OPTIONS;

  results: any[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  menuVisible = false;
  constructor(private readonly fb: FormBuilder, private readonly supplierService: SupplierService, private readonly authService: AuthService) {
    this.form = this.fb.group({
      fields: this.fb.array([this.createField()])
    });
  }

  ngOnInit(): void {
    const account = this.authService.activeAccount;

    if (!account) {
      this.authService.login();
    }
    this.isLoading = true;
    setTimeout(() => {
      this.isLoading = false;
    }, 500);

    this.form = this.fb.group({
      fields: this.fb.array([this.createRow('', 'Supplier Name')]) // Default field set
    });
    this.dataSource.sort = this.sort;
  }

  ngAfterViewInit() {
    this.suppliers.sort = this.sort;
  }

  //Create a new form group
  createField(operator: string = '', showButtons: boolean = true): FormGroup {
    return this.fb.group({
      selectedField: ['', Validators.required],
      //operator: ['Contains'],
      fieldValue: [''],
      logicalOperator: [operator],
      showButtons: [showButtons]
    });
  }
  get fields(): FormArray {
    return this.form.get('fields') as FormArray;
  }

  private createRow(operator: '' | 'AND' | 'OR', selectedField: string = ''): FormGroup {
    return this.fb.group({
      logicalOperator: [operator],
      selectedField: [selectedField],
      operator: ['Contains'],
      fieldValue: ['', Validators.required],
      showButtons: [true]
    });
  }

  createFieldGroup(): FormGroup {
    return this.fb.group({
      selectedField: ['', Validators.required],
      operator: ['Contains', Validators.required],
      fieldValue: ['', Validators.required],
      logicalOperator: ['']  // default value (optional)
    });
  }

  addFieldWithOperator(op: 'AND' | 'OR', idx: number) {
    const arr = this.fields;

    // compute insert index (OR inline, AND always at end)
    const insertAt = op === 'AND'
      ? arr.length
      : (() => {
        let i = idx + 1;
        while (i < arr.length && arr.at(i).value.logicalOperator === 'OR') { i++; }
        return i;
      })();

    // insert row
    arr.insert(insertAt, this.createRow(op));

    // if OR, copy previous row’s field/value
    if (op === 'OR') {
      this.showOperator = true;

      const prev = arr.at(insertAt - 1).value;
      arr.at(insertAt).patchValue({
        selectedField: prev.selectedField,
        operator: prev.operator,
        showButtons: false
      });

      // hide buttons after
      for (let i = insertAt + 1; i < arr.length; i++) {

        const nextOp = arr.at(i).get('logicalOperator')?.value;
        if (nextOp === 'AND') {
          // stop hiding once we reach an AND row
          break;
        }
        arr.at(i).patchValue({ showButtons: false });
      }
    }
    // AND always shows buttons on new bottom row
  }

  //Remove the created row
  removeField(idx: number) {
    this.fields.removeAt(idx);
    // recalc showButtons for subsequent rows
    if (idx > 0 && idx < this.fields.length) {
      const prevOp = this.fields.at(idx - 1).value.logicalOperator;
      const show = prevOp !== 'OR';
      for (let i = idx; i < this.fields.length; i++) {
        this.fields.at(i).patchValue({ showButtons: show });
      }
    }
    // if no AND-capable row left, reset
    const anyAnd = this.fields.controls.some(fg =>
      fg.value.showButtons && fg.value.logicalOperator !== 'OR'
    );
    if (!anyAnd) {
      this.form.setControl('fields', this.fb.array([this.createRow('')]));
    }
  }

  //Call search function when click on search button
  search() {
    this.pageNumber = 1;

    if (this.paginator) {
      this.paginator.pageIndex = 0;   // Reset to first page
    }
    this.suppliers.data = [];
    const fieldArray = this.fields.value;  // fields is your FormArray
    const summaryParts = fieldArray
      .filter((field: any) => field.selectedField && field.selectedField.trim() !== '')
      .map((field: any) => {
        const value = field.fieldValue?.toString().trim() || "(empty)";
        return `${field.selectedField}=${value}`;
      });

    const model = {
      ...this.form.value,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize
    };
    this.searchSuppliers(model)
  }

  //Consume the search criteria model
  searchSuppliers(model: any): void {

    this.totalRecords = 0;
    this.isLoading = true;

    this.supplierService.getSuppliers(model).subscribe({
      next: (response: SupplierResponse) => {

        this.suppliers.data = response.suppliers;

        this.totalRecords = response.totalRecords;

        this.hasRecords = response.suppliers.length === 0;

        this.isLoading = false;
      },
      error: (error) => {

        console.error('Error fetching suppliers', error);

        this.isLoading = false;
      }
    });
  }

  isLastRow(index: number): boolean {
    return index === this.fields.length - 1;
  }
  onPageChange(event: PageEvent): void {

    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;

    const model = {
      ...this.form.value,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize
    };

    this.searchSuppliers(model);
  }
}
